import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import BackButton from "../../../components/Button/BackButton";
import ConfirmModal from "../../../components/Modals/ConfirmModal";
import { toast } from "../../../components/Toast/toast";
import { btn } from "../../../components/Button/buttonStyles";
import { adjustmentLabel } from "../../../utils/adjustments";

const fmtNum = (v) =>
  v !== undefined && v !== null && v !== "" ? Number(v).toFixed(2) : "—";

const fmtDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleString();
};

const Field = ({ label, value, children }) => (
  <div className="flex">
    <span className="w-52 font-medium text-gray-700 shrink-0">{label}:</span>
    <span className="text-gray-900">{children ?? value ?? "—"}</span>
  </div>
);

const SectionTitle = ({ children }) => (
  <p className="text-left font-bold text-base text-black-600 pt-2 pb-2">{children}</p>
);

const PayableDetailPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type") || "flat_rate";
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const endpoint =
    type === "unit_based"
      ? `/project-finances/unit-based-payables/${id}`
      : `/project-finances/flat-rate-payables/${id}`;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(endpoint, { withCredentials: true });
      setData(res.data.data);
    } catch (err) {
      console.error(err);
      setError("Failed to load payable details.");
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    if (id) fetchData();
  }, [id, endpoint, fetchData]);

  const handleDelete = async () => {
    try {
      await api.delete(endpoint, { withCredentials: true });
      navigate("/vendor/payables");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to delete payable.");
    } finally {
      setShowDeleteModal(false);
    }
  };

  const handleEdit = () => {
    const path =
      type === "unit_based"
        ? `/project/${data.project_id}/job/${data.job_id}/edit-unit-based-receivable/${id}`
        : `/project/${data.project_id}/job/${data.job_id}/edit-flat-rate-receivable/${id}`;
    navigate(path);
  };

  const handleClone = async () => {
    const cloneEndpoint =
      type === "unit_based"
        ? "/project-finances/unit-based-payables"
        : "/project-finances/flat-rate-payables";

    const {
      id: _id, createdAt, updatedAt,
      project, job, currency, unit, file, vendorInvoice,
      extra_charge, discount, total,
      ...payload
    } = data;

    try {
      const jobRes = await api.get(`/jobs/${data.job_id}`, { withCredentials: true });
      const jobData = jobRes.data.data;

      const res = await api.post(
        cloneEndpoint,
        {
          ...payload,
          project_id: Number(data.project_id),
          job_id: Number(data.job_id),
          client_id: jobData.project?.client_id,
          vendor_id: jobData.vendor_id,
        },
        { withCredentials: true }
      );
      if (res.data.success) {
        navigate(`/vendor/payable/${res.data.data.id}?type=${type}`);
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to clone payable.");
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center mt-16">
        <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
      </div>
    );
  if (error)
    return <div className="text-center mt-10 text-danger-600 font-medium">{error}</div>;
  if (!data)
    return <div className="text-center mt-10 text-gray-500">Payable not found.</div>;

  const isInvoiced = !!data.vendorInvoice;
  const code = data.payable_code || "";

  const vendorName = (() => {
    const v = data.job?.vendor;
    if (!v) return "—";
    if (v.type === "Company") return v.company_name || "—";
    const u = v.primary_users;
    return u ? `${u.first_name} ${u.last_name}`.trim() : "—";
  })();

  const langPair = data.job?.languagePair
    ? `${data.job.languagePair.sourceLanguage?.name || ""} - ${data.job.languagePair.targetLanguage?.name || ""}`
    : "—";

  const currCode = data.currency?.currency?.code || "—";

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <BackButton />
          <h2 className="text-[22px] font-semibold tracking-tight text-gray-900">Payable {code}</h2>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleClone}
            className={btn("secondary", "sm")}
          >
            Clone
          </button>
          <button
            onClick={() => navigate(`/vendor/payable/${id}/adjustments?type=${type}`)}
            className={btn("secondary", "sm")}
          >
            Price adjustments
          </button>
          <button
            onClick={handleEdit}
            disabled={isInvoiced}
            title={isInvoiced ? "Cannot edit an invoiced payable" : undefined}
            className={btn("primary", "sm")}
          >
            Update
          </button>
          <button
            onClick={() => !isInvoiced && setShowDeleteModal(true)}
            disabled={isInvoiced}
            title={isInvoiced ? "Cannot delete an invoiced payable" : undefined}
            className={btn("danger", "sm")}
          >
            Delete
          </button>
        </div>
      </div>

      {/* Invoiced notice */}
      {isInvoiced && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
          This payable has been invoiced (
          <Link
            to={`/vendor/invoice/${data.vendorInvoice.id}`}
            className="font-semibold underline hover:text-amber-900"
          >
            {data.vendorInvoice.invoice_code}
          </Link>
          ) and cannot be edited or deleted.
        </div>
      )}

      {/* Detail card */}
      <div className="bg-white shadow rounded-lg p-8 space-y-4 text-sm">
        <Field label="Invoice">
          {data.vendorInvoice ? (
            <Link
              to={`/vendor/invoice/${data.vendorInvoice.id}`}
              className="text-brand-600 hover:text-brand-800 underline font-medium"
            >
              {data.vendorInvoice.invoice_code}
            </Link>
          ) : "—"}
        </Field>
        <Field label="Job vendor">
          {vendorName}
        </Field>

        <SectionTitle>Service</SectionTitle>

        <Field label="Job service" value={data.job?.service?.name || "—"} />
        <Field label="Job language pair" value={langPair} />
        <Field label="Project specialization" value={data.project?.specialization?.name || "—"} />

        <SectionTitle>Subtotal &amp; total</SectionTitle>

        {type === "unit_based" && (
          <>
            <Field label="Unit amount" value={fmtNum(data.unit_amount)} />
            <Field label="Unit" value={data.unit?.name || "—"} />
            <Field label="Price per unit" value={fmtNum(data.price_per_unit)} />
          </>
        )}
        <Field label="Subtotal" value={fmtNum(data.subtotal)} />
        <Field label="Extra charge" value={adjustmentLabel(data, "extra_charge")} />
        <Field label="Discount" value={adjustmentLabel(data, "discount")} />
        <Field label="Total" value={fmtNum(data.total)} />
        <Field label="Currency" value={currCode} />

        <SectionTitle>Other details</SectionTitle>

        <Field label="Filename" value={data.file?.file_name || data.file?.file_code || "—"} />
        <Field label="Note for vendor" value={data.note_for_vendor || "—"} />
        <Field label="Internal note" value={data.internal_note || "—"} />
        <Field label="Issued at" value={fmtDate(data.createdAt)} />
        <Field label="Created at" value={fmtDate(data.createdAt)} />
      </div>

      {showDeleteModal && (
        <ConfirmModal
          title="Confirm Delete"
          message={`Are you sure you want to delete payable ${code}?`}
          onCancel={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          confirmText="Yes, Delete"
        />
      )}
    </div>
  );
};

export default PayableDetailPage;
